import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, Paperclip, Printer, XCircle } from 'lucide-react';
import type { OrderLinesPage, OrderLineView } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Skeleton, StatusBadge } from '@/components/ui/primitives';
import { ReasonDialog } from '@/components/ui/reason-dialog';
import { useToast } from '@/components/ui/toast';
import { SourceStateBadge } from '../../SourceStateBadge';
import { isAdmissionScope } from './draft';
import type { OrderKind, OrderScope } from './types';

const PAGE_SIZE = 20;

export function orderLinesKey(scope: OrderScope, kind?: OrderKind): unknown[] {
  return ['order-lines', scope.encounterId ?? scope.admissionId, ...(kind ? [kind] : [])];
}

export function BillingBadge({ line }: { line: Pick<OrderLineView, 'billing' | 'fulfilment'> }) {
  if (line.fulfilment === 'outside') return null;
  if (line.billing === 'paid') return <StatusBadge status="paid" label={tr('opd', 'order_billing_paid')} />;
  if (line.billing === 'unpaid') return <StatusBadge status="awaiting" label={tr('opd', 'order_billing_unpaid')} />;
  return <StatusBadge status="draft" tone="muted" label={tr('opd', 'order_billing_not_billed')} />;
}

async function printFrom(path: string, onError: (caught: unknown) => void) {
  try {
    const doc = await get<{ html: string }>(path);
    printDocument(doc.html);
  } catch (caught) {
    onError(caught);
  }
}

function reportPath(report: OrderLineView['reports'][number]): string {
  return report.kind === 'lab' ? `/lab/orders/${report.id}/print` : `/radiology/studies/${report.id}/print`;
}

function LineRow({
  line,
  readOnly,
  admission,
  onCancel,
  onComplete,
  onAttach,
}: {
  line: OrderLineView;
  readOnly: boolean;
  admission: boolean;
  onCancel: (line: OrderLineView) => void;
  onComplete: (line: OrderLineView) => void;
  onAttach?: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const cancelled = line.status === 'cancelled';
  const outside = line.fulfilment === 'outside';
  const failed = (caught: unknown) => toast.error(tr('opd', 'emr_could_not_print'), describeError(caught));
  return (
    <li className="rounded-xl border bg-card p-3 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={cancelled ? 'font-medium text-muted-foreground line-through' : 'font-medium'}>
            {line.label}
            {line.quantity > 1 && <span className="ms-1.5 text-xs font-normal text-muted-foreground tabular">×{line.quantity}</span>}
          </p>
          <p className="text-xs text-muted-foreground">
            {dateTime(line.orderedAt)}
            {line.priority !== 'routine' && <span className="ms-1.5 font-medium text-warning">· {tr('opd', line.priority)}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={line.status} />
          <Badge variant={outside ? 'warning' : 'info'}>{outside ? tr('opd', 'order_outside') : tr('opd', 'order_done_here')}</Badge>
          {!cancelled && <BillingBadge line={line} />}
          {line.insured && !outside && <Badge variant="secondary">{tr('opd', 'emr_insured')}</Badge>}
          {!cancelled && !outside && <SourceStateBadge sourceKeys={line.sourceKeys} />}
        </div>
      </div>
      {line.notes && <p className="mt-1 text-xs text-muted-foreground">{line.notes}</p>}
      {line.cancelReason && <p className="mt-1 text-xs text-destructive">{tr('opd', 'order_cancelled_x', { reason: line.cancelReason })}</p>}
      {outside && !cancelled && <p className="mt-1 text-xs text-muted-foreground">{tr('opd', 'order_outside_hint')}</p>}
      {!admission && !cancelled && line.billing === 'unpaid' && line.type !== 'procedure' && <p className="mt-1 text-xs text-warning">{tr('opd', 'order_waits_for_payment')}</p>}
      <div className="mt-2 flex flex-wrap justify-end gap-1">
        {line.reports.map((report) => (
          <Button key={report.id} size="sm" variant="ghost" onClick={() => void printFrom(reportPath(report), failed)}>
            <FileText className="h-4 w-4" aria-hidden /> {tr('opd', 'view_report')}
          </Button>
        ))}
        {!cancelled && (
          <Button size="sm" variant="ghost" onClick={() => void printFrom(`/clinical-orders/${line.clinicalOrderId}/requisition`, failed)}>
            <Printer className="h-4 w-4" aria-hidden /> {tr('opd', 'order_print_requisition')}
          </Button>
        )}
        {outside && !cancelled && onAttach && (
          <Button size="sm" variant="ghost" onClick={onAttach}>
            <Paperclip className="h-4 w-4" aria-hidden /> {tr('opd', 'order_attach_result')}
          </Button>
        )}
        {!readOnly && line.type === 'procedure' && !outside && line.status === 'ordered' && can('order.write', 'vitals.write') && (
          <Button size="sm" variant="outline" onClick={() => onComplete(line)}>
            <CheckCircle2 className="h-4 w-4" aria-hidden /> {tr('opd', 'done')}
          </Button>
        )}
        {!readOnly && line.cancellable && can('order.write') && (
          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => onCancel(line)}>
            <XCircle className="h-4 w-4" aria-hidden /> {tr('opd', 'order_cancel_line')}
          </Button>
        )}
      </div>
    </li>
  );
}

export function OrderLinesList({ kind, scope, readOnly, onChanged, onAttach }: { kind: OrderKind; scope: OrderScope; readOnly: boolean; onChanged: () => void; onAttach?: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const admission = isAdmissionScope(scope);
  const [cancelling, setCancelling] = useState<OrderLineView | null>(null);
  const [completing, setCompleting] = useState<OrderLineView | null>(null);
  const lines = useInfiniteQuery({
    queryKey: orderLinesKey(scope, kind),
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) =>
      get<OrderLinesPage>(`/clinical-orders/lines${qs({ ...scope, type: kind, limit: PAGE_SIZE, cursor: pageParam || undefined })}`, signal),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = lines.data?.pages.flatMap((page) => page.data) ?? [];
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: orderLinesKey(scope) });
    onChanged();
  };

  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => post<{ refundNeeded: boolean }>(`/clinical-orders/lines/${id}/cancel`, { reason }),
    onSuccess: (outcome) => {
      setCancelling(null);
      toast.success(outcome.refundNeeded ? tr('opd', 'order_cancelled_refund') : tr('opd', 'order_cancelled'));
      refresh();
    },
    onError: (caught) => toast.error(tr('opd', 'order_could_not_cancel'), describeError(caught)),
  });

  const complete = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => post(`/clinical-orders/${id}/complete`, { note }),
    onSuccess: () => {
      setCompleting(null);
      refresh();
    },
    onError: (caught) => toast.error(tr('opd', 'could_not_record_the_procedure'), describeError(caught)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('opd', admission ? `io_ord_lines_${kind}` : `order_lines_${kind}`)}</CardTitle>
      </CardHeader>
      <CardContent>
        {lines.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : lines.error && rows.length === 0 ? (
          <p className="text-sm text-destructive">{describeError(lines.error)}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{tr('opd', 'order_lines_empty')}</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((line) => (
              <LineRow key={line.id} line={line} readOnly={readOnly} admission={admission} onCancel={setCancelling} onComplete={setCompleting} onAttach={onAttach} />
            ))}
          </ul>
        )}
        {lines.hasNextPage && (
          <div className="mt-3 flex justify-center">
            <Button size="sm" variant="outline" onClick={() => void lines.fetchNextPage()} loading={lines.isFetchingNextPage}>
              {tr('opd', 'order_load_more')}
            </Button>
          </div>
        )}
      </CardContent>
      {cancelling && (
        <ReasonDialog
          title={tr('opd', 'order_cancel_title', { name: cancelling.label })}
          description={tr('opd', 'order_cancel_description')}
          minLength={3}
          maxLength={400}
          submitLabel={tr('opd', 'order_cancel_line')}
          destructive
          loading={cancel.isPending}
          onSubmit={(reason) => cancel.mutate({ id: cancelling.id, reason })}
          onClose={() => setCancelling(null)}
        />
      )}
      {completing && (
        <ReasonDialog
          title={tr('opd', 'procedure_note_title')}
          description={tr('opd', 'procedure_note_description')}
          label={tr('opd', 'procedure_note')}
          minLength={3}
          maxLength={4000}
          submitLabel={tr('opd', 'done')}
          loading={complete.isPending}
          onSubmit={(note) => complete.mutate({ id: completing.clinicalOrderId, note })}
          onClose={() => setCompleting(null)}
        />
      )}
    </Card>
  );
}
